import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import {
  COMPANY, REGIONS, HERO_IMAGES, WHY_CHOOSE,
  ENTERPRISE_INDUSTRIES, EXPERTISE_AREAS, DELIVERY_MODEL,
} from "@/data/content";
import { Check } from "lucide-react";

export default function About() {
  return (
    <>
      <SEO title="About Us" description="DataMeridian Consulting — a boutique enterprise technology consulting company for database engineering, cloud modernization and production database support." />

      <section className="bg-[var(--dm-navy)] text-white">
        <div className="dm-container py-24 lg:py-32 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="dm-overline text-[var(--dm-emerald)]">About DataMeridian</div>
            <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] mt-6">A boutique consulting company for mission-critical database environments.</h1>
            <p className="text-white/75 text-lg mt-8 max-w-2xl leading-relaxed">
              {COMPANY.positioning}
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="dm-container dm-section grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6">
            <div className="dm-overline">Who we are</div>
            <h2 className="font-serif text-4xl mt-4 text-[var(--dm-navy)] leading-tight">Consulting expertise developed over more than a decade.</h2>
            <p className="text-[var(--dm-muted)] mt-6 leading-relaxed">
              DataMeridian Consulting is a boutique technology consulting company helping organizations design, modernize, optimize and support mission-critical database environments.
            </p>
            <p className="text-[var(--dm-muted)] mt-4 leading-relaxed">
              Our consulting expertise has been developed through more than 12 years of working on enterprise database environments supporting multinational organizations across manufacturing, logistics, supply chain and technology industries.
            </p>
            <p className="text-[var(--dm-muted)] mt-4 leading-relaxed">
              We help organizations modernize legacy database platforms, optimize database performance, migrate workloads to cloud platforms and build reliable production database environments. Every engagement focuses on measurable business outcomes, long-term reliability, operational excellence and scalable architecture.
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="aspect-[4/3] overflow-hidden border border-[var(--dm-border)]">
              <img src={HERO_IMAGES.about} alt="DataMeridian" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ENTERPRISE EXPERIENCE */}
      <section className="bg-[var(--dm-surface)]">
        <div className="dm-container dm-section grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="dm-overline">Enterprise Experience</div>
            <h2 className="font-serif text-4xl mt-4 text-[var(--dm-navy)] leading-tight">Industries our consulting expertise has been developed across.</h2>
            <ul className="mt-8 grid grid-cols-2 gap-2">
              {ENTERPRISE_INDUSTRIES.map((i) => (
                <li key={i} className="flex gap-2 text-sm text-[var(--dm-navy)]">
                  <Check className="w-4 h-4 mt-1 text-[var(--dm-emerald)] shrink-0" /> {i}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="dm-overline">Areas of Expertise</div>
            <div className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {EXPERTISE_AREAS.map((a) => (
                <div key={a} className="flex gap-2 text-sm text-[var(--dm-navy)] border-b border-[var(--dm-border)] pb-2">
                  <span className="text-[var(--dm-emerald)] font-mono text-xs mt-1">·</span> {a}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY DATAMERIDIAN */}
      <section className="bg-white">
        <div className="dm-container dm-section">
          <div className="dm-overline">Why DataMeridian</div>
          <h2 className="font-serif text-4xl mt-4 text-[var(--dm-navy)] max-w-2xl leading-tight">Meaningful strengths — not fabricated numbers.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {WHY_CHOOSE.map((w) => (
              <div key={w.title} className="border border-[var(--dm-border)] p-6">
                <Check className="w-5 h-5 text-[var(--dm-emerald)]" />
                <div className="font-serif text-lg text-[var(--dm-navy)] mt-3">{w.title}</div>
                <div className="text-sm text-[var(--dm-muted)] mt-2 leading-relaxed">{w.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DELIVERY MODEL */}
      <section className="bg-[var(--dm-navy)] text-white">
        <div className="dm-container dm-section">
          <div className="dm-overline text-white/60">Our Delivery Model</div>
          <h2 className="font-serif text-4xl mt-4 max-w-3xl leading-tight">A structured path — Discovery to Continuous Support.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mt-14">
            {DELIVERY_MODEL.map((p) => (
              <div key={p.step} className="bg-[var(--dm-navy)] p-8">
                <div className="font-mono text-xs text-[var(--dm-emerald)]">{p.step}</div>
                <div className="font-serif text-xl mt-3">{p.title}</div>
                <div className="text-sm text-white/70 mt-2 leading-relaxed">{p.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REGIONS */}
      <section className="bg-white">
        <div className="dm-container dm-section">
          <div className="dm-overline">Regions Served</div>
          <h2 className="font-serif text-4xl mt-4 text-[var(--dm-navy)] max-w-3xl leading-tight">Remote consulting worldwide.</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-10">
            {REGIONS.map((r) => (
              <div key={r} className="text-sm text-[var(--dm-navy)] border-l border-[var(--dm-border)] pl-4">{r}</div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
